import metadata from './block.json';
import { useState, useEffect } from '@wordpress/element';
import { registerCheckoutBlock } from '@woocommerce/blocks-checkout';
import { extensionCartUpdate } from '@woocommerce/blocks-checkout';
import apiFetch from '@wordpress/api-fetch';
import { useSelect } from '@wordpress/data';
import { __ } from '@wordpress/i18n';

const Block = ({ children, checkoutExtensionData }) => {
	const [selectedInsurance, setSelectedInsurance] = useState('');
	const [insurancePackages, setInsurancePackages] = useState([]);
	const [filteredPackages, setFilteredPackages] = useState([]);
	const [settings, setSettings] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [termsPageUrl, setTermsPageUrl] = useState('');
	const [shippingZones, setShippingZones] = useState({});

	// Fetch shipping zones data on mount
	useEffect(() => {
		if ( window.shippingInsuranceData && window.shippingInsuranceData.shippingZones ) {
			setShippingZones( window.shippingInsuranceData.shippingZones );
		}
	}, []);

	// Get current shipping zone, class, and user role
	const { shippingCountry, billingCountry, shippingClasses, userRole, userMemberships } = useSelect(
		(select) => {
			const store = select('wc/store/cart');
			// Get shipping and billing data from the cart store.
			const customerData = store?.getCustomerData?.() || {};
			const shippingAddress = customerData.shippingAddress || {};
			const billingAddress = customerData.billingAddress || {};
			// Get shipping classes from localized data; fall back to extension data.
			const extClasses = checkoutExtensionData?.shippingData?.shippingRates?.[0]?.shipping_classes || [];
			const uniqueClasses = [...new Set(extClasses.filter(Boolean))];
			// Use the localized userMemberships from shippingInsuranceData.
			const memberships = window.shippingInsuranceData?.userMemberships || [];
			return {
				shippingCountry: shippingAddress.country || 'all',
				billingCountry: billingAddress.country || 'all',
				shippingClasses: window.shippingInsuranceData?.shippingClasses || (uniqueClasses.length ? uniqueClasses : ['all']),
				userRole: window.shippingInsuranceData?.userRole || customerData.role || 'all',
				userMemberships: memberships
			};
		},
		(oldValues, newValues) =>
			oldValues.shippingCountry === newValues.shippingCountry &&
			oldValues.billingCountry === newValues.billingCountry &&
			JSON.stringify(oldValues.shippingClasses) === JSON.stringify(newValues.shippingClasses) &&
			oldValues.userRole === newValues.userRole &&
			JSON.stringify(oldValues.userMemberships) === JSON.stringify(newValues.userMemberships)
	);

	// Get shipping data from WooCommerce store
	const { selectedShippingMethod, cartTotal } = useSelect((select) => {
		const store = select('wc/store/cart');
		const shippingRates = store?.getShippingRates?.() || [];
		const selectedRates = shippingRates[0]?.shipping_rates?.filter(rate => rate.selected) || [];
		// Get cart totals
		const cartTotals = store?.getCartTotals?.() || {};
		// Use total_price instead of total_items to get the actual cart total
		const total = parseFloat(cartTotals.total_price || cartTotals.total_items) || 0;
		return {
			selectedShippingMethod: selectedRates[0] || null,
			cartTotal: total
		};
	});
	
	useEffect(() => {
	    if (!isLoading && settings && filteredPackages.length > 0) {
	        const original = window.shippingInsuranceData ? String(window.shippingInsuranceData.selected_insurance) : "";
	        
	        // Check if the current selection is valid
	        const currentSelectionValid = selectedInsurance === "" || filteredPackages.find(pkg => String(pkg.id) === selectedInsurance);
	        
	        if (!currentSelectionValid) {
	            // If original selection is valid (including "No insurance"), use it
	            if (original === "0" || original === "") {
	                setSelectedInsurance("");
	                extensionCartUpdate({
	                    namespace: 'shipping-insurance-manager',
	                    data: {
	                        selected_insurance: "",
	                        package_data: null,
	                        is_taxable: settings?.taxable || true,
	                        tax_class: settings?.taxable ? settings?.tax_class || '' : ''
	                    }
	                });
	            } else if (original && filteredPackages.find(pkg => String(pkg.id) === original)) {
	                setSelectedInsurance(original);
	                extensionCartUpdate({
	                    namespace: 'shipping-insurance-manager',
	                    data: {
	                        selected_insurance: original,
	                        package_data: filteredPackages.find(pkg => String(pkg.id) === original),
	                        is_taxable: settings?.taxable || true,
	                        tax_class: settings?.taxable ? settings?.tax_class || '' : ''
	                    }
	                });
	            }
	        }
	    }
	}, [filteredPackages, isLoading, settings, selectedInsurance]);

	// Load both packages and settings
	useEffect(() => {
		if ( window.shippingInsuranceData ) {
			// Use either 'insurancePackages' or fallback to 'packages' based on what's provided.
			const packages = window.shippingInsuranceData.insurancePackages || window.shippingInsuranceData.packages || [];
			setInsurancePackages(packages);
			setSettings(window.shippingInsuranceData.settings || {});
			setTermsPageUrl((window.shippingInsuranceData.settings && window.shippingInsuranceData.settings.terms_page_url) || '');

			// Handle default insurance selection
			const defaultInsurance = window.shippingInsuranceData.selected_insurance;
			const defaultPackageOption = window.shippingInsuranceData.settings?.default_package || '';

			// Calculate package values for sorting
			const packageValues = packages.map(pkg => {
				let value;
				if (pkg.type === 'fixed') {
					value = parseFloat(pkg.amount);
				} else if (pkg.type === 'percentage') {
					// For percentage, we'll use the raw percentage value for comparison
					value = parseFloat(pkg.amount);
				}
				return { ...pkg, calculatedValue: value };
			});

			if (defaultInsurance === 0 || defaultInsurance === "0") {
				// Explicitly selected "No insurance"
				setSelectedInsurance("");
			} else if (defaultPackageOption === 'most_expensive' && packageValues.length > 0) {
				// Find most expensive package
				const mostExpensive = [...packageValues].sort((a, b) => b.calculatedValue - a.calculatedValue)[0];
				setSelectedInsurance(String(mostExpensive.id));
				// Update cart immediately
				extensionCartUpdate({
					namespace: 'shipping-insurance-manager',
					data: {
						selected_insurance: String(mostExpensive.id),
						package_data: mostExpensive,
						is_taxable: settings?.taxable || true,
						tax_class: settings?.taxable ? settings?.tax_class || '' : ''
					}
				});
			} else if (defaultPackageOption === 'least_expensive' && packageValues.length > 0) {
				// Find least expensive package
				const leastExpensive = [...packageValues].sort((a, b) => a.calculatedValue - b.calculatedValue)[0];
				setSelectedInsurance(String(leastExpensive.id));
				// Update cart immediately
				extensionCartUpdate({
					namespace: 'shipping-insurance-manager',
					data: {
						selected_insurance: String(leastExpensive.id),
						package_data: leastExpensive,
						is_taxable: settings?.taxable || true,
						tax_class: settings?.taxable ? settings?.tax_class || '' : ''
					}
				});
			} else if (defaultInsurance) {
				// Specific package selected
				const matched = packages.find(pkg => String(pkg.id) === String(defaultInsurance));
				setSelectedInsurance(matched ? String(defaultInsurance) : "");
				if (matched) {
					// Update cart immediately
					extensionCartUpdate({
						namespace: 'shipping-insurance-manager',
						data: {
							selected_insurance: String(defaultInsurance),
							package_data: matched,
							is_taxable: settings?.taxable || true,
							tax_class: settings?.taxable ? settings?.tax_class || '' : ''
						}
					});
				}
			} else {
				// No selection or default package option, default to no insurance
				setSelectedInsurance("");
				// Update cart immediately to clear any insurance
				extensionCartUpdate({
					namespace: 'shipping-insurance-manager',
					data: {
						selected_insurance: "",
						package_data: null,
						is_taxable: settings?.taxable || true,
						tax_class: settings?.taxable ? settings?.tax_class || '' : ''
					}
				});
			}
		}
		setIsLoading(false);
	}, []);

	// Filter packages based on restrictions with debug logging
	useEffect(() => {
		if (!insurancePackages.length || !settings) {
			return;
		}

		// First check if current shipping method is globally excluded.
		if (
			selectedShippingMethod &&
			settings.excluded_shipping_methods.includes(selectedShippingMethod.method_id)
		) {
			setFilteredPackages([]);
			return;
		}
		const filtered = insurancePackages.filter(pkg => {
			const { restrictions, min_cart_value } = pkg;
			// --- Shipping Zone Check ---
			let zoneAllowed = false;
			if (Array.isArray(restrictions.shipping_zones)) {
				zoneAllowed =
					restrictions.shipping_zones.includes('all') ||
					restrictions.shipping_zones.some(zoneId => {
						const allowed = shippingZones[zoneId] && shippingZones[zoneId].includes(shippingCountry);
						return allowed;
					});
			} else {
				zoneAllowed = restrictions.shipping_zones === 'all' ||
					shippingZones[restrictions.shipping_zones]?.includes(shippingCountry);
			}
			// --- Shipping Class Check ---
			let classAllowed = false;
			if (Array.isArray(restrictions.shipping_classes)) {
				classAllowed =
					restrictions.shipping_classes.includes('all') ||
					shippingClasses.some(cls => restrictions.shipping_classes.includes(cls.toString()));
			} else {
				classAllowed = restrictions.shipping_classes === 'all' ||
					restrictions.shipping_classes === shippingClasses[0];
			}
			// --- User Role Check (with normalization) ---
			let roleAllowed = false;
			if (Array.isArray(restrictions.user_roles)) {
				const normalizedUserRole = userRole.toLowerCase();
				roleAllowed =
					restrictions.user_roles.includes('all') ||
					restrictions.user_roles.some(role => role.toLowerCase() === normalizedUserRole);
			} else {
				roleAllowed = restrictions.user_roles === 'all' ||
					restrictions.user_roles.toLowerCase() === userRole.toLowerCase();
			}
			// --- Membership Check ---
			let membershipAllowed = false;
			if (Array.isArray(restrictions.memberships)) {
				membershipAllowed = restrictions.memberships.includes('all') ||
					userMemberships.some(mem => restrictions.memberships.includes(mem));
			} else {
				membershipAllowed = restrictions.memberships === 'all' ||
					userMemberships.includes(restrictions.memberships);
			}
			// --- Minimum Cart Value Check ---
			const minimumAllowed = !min_cart_value || cartTotal >= parseFloat(min_cart_value);
			const isAllowed = zoneAllowed && classAllowed && roleAllowed && membershipAllowed && minimumAllowed;
			return isAllowed;
		});
		setFilteredPackages(filtered);
	}, [
		insurancePackages,
		selectedShippingMethod,
		settings,
		shippingZones,
		shippingCountry,
		shippingClasses,
		userRole,
		userMemberships,
		cartTotal,
		selectedInsurance
	]);

	// New effect to update the order summary on page load
	useEffect(() => {
		if (!isLoading && settings) {
			const taxable = settings?.taxable || true;
			const taxClass = taxable ? settings?.tax_class || '' : '';
			
			// Check if we need to set a default selection based on settings
			if (selectedInsurance === "" && settings.default_package) {
				const defaultPackageOption = settings.default_package;
				
				// Calculate package values for sorting
				const packageValues = insurancePackages.map(pkg => {
					let value;
					if (pkg.type === 'fixed') {
						value = parseFloat(pkg.amount);
					} else if (pkg.type === 'percentage') {
						// For percentage, we'll use the raw percentage value for comparison
						value = parseFloat(pkg.amount);
					}
					return { ...pkg, calculatedValue: value };
				});
				
				if (defaultPackageOption === 'most_expensive' && packageValues.length > 0) {
					// Find most expensive package
					const mostExpensive = [...packageValues].sort((a, b) => b.calculatedValue - a.calculatedValue)[0];
					setSelectedInsurance(String(mostExpensive.id));
					extensionCartUpdate({
						namespace: 'shipping-insurance-manager',
						data: {
							selected_insurance: String(mostExpensive.id),
							package_data: mostExpensive,
							is_taxable: taxable,
							tax_class: taxClass
						}
					});
					return; // Exit early since we've updated the selection
				} else if (defaultPackageOption === 'least_expensive' && packageValues.length > 0) {
					// Find least expensive package
					const leastExpensive = [...packageValues].sort((a, b) => a.calculatedValue - b.calculatedValue)[0];
					setSelectedInsurance(String(leastExpensive.id));
					extensionCartUpdate({
						namespace: 'shipping-insurance-manager',
						data: {
							selected_insurance: String(leastExpensive.id),
							package_data: leastExpensive,
							is_taxable: taxable,
							tax_class: taxClass
						}
					});
					return; // Exit early since we've updated the selection
				}
			}
			
			// If no default selection was made, update with current selection
			extensionCartUpdate({
				namespace: 'shipping-insurance-manager',
				data: {
					selected_insurance: selectedInsurance, // Preserve current selection even if it is empty
					package_data: selectedInsurance === "" ? null : insurancePackages.find(pkg => pkg.id.toString() === selectedInsurance),
					is_taxable: taxable,
					tax_class: taxClass
				}
			});
		}
	}, [isLoading, settings, selectedInsurance, insurancePackages]);

	const handleChange = (event) => {
		const value = event.target.value;
		setSelectedInsurance(value);
		// Get the tax settings
		const taxable = settings?.taxable || true;
		let taxClass = '';
		if (taxable) {
			taxClass = settings?.tax_class || '';
		}
		// Update the cart with the selected insurance and tax information
		extensionCartUpdate({
			namespace: 'shipping-insurance-manager',
			data: {
				selected_insurance: value,
				package_data: value ? insurancePackages.find(pkg => pkg.id.toString() === value) : null,
				is_taxable: taxable,
				tax_class: taxClass
			}
		});
	};

	if (isLoading) {
		return null;
	}

	// Don't render if no shipping method selected or if method is excluded
	if (!selectedShippingMethod ||
		(settings?.excluded_shipping_methods || []).includes(selectedShippingMethod.method_id) ||
		!filteredPackages.length) {
		return null;
	}

	return (
		<div className="wc-block-components-shipping-rates-control">
			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				marginBottom: '8px',
				marginTop: '2.5em'
			}}>
				<h2 className="wc-block-components-title">
					{__('Shipping Insurance', 'shipping-insurance-manager')}
				</h2>
				{termsPageUrl && (
					<a href={termsPageUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="wc-block-components-link"
						style={{
							fontSize: '0.875em',
							color: '#757575',
							textDecoration: 'none',
							display: 'flex',
							alignItems: 'center',
							gap: '4px'
						}}>
						{__('Insurance Terms', 'shipping-insurance-manager')}
						<span className="screen-reader-text">
							{__('(opens in a new tab)', 'shipping-insurance-manager')}
						</span>
						<svg xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							width="16"
							height="16"
							fill="currentColor"
							style={{ marginTop: '2px' }}>
							<path d="M18.2 17c0 .7-.6 1.2-1.2 1.2H7c-.7 0-1.2-.6-1.2-1.2V7c0-.7.6-1.2 1.2-1.2h3.2V4.2H7C5.5 4.2 4.2 5.5 4.2 7v10c0 1.5 1.2 2.8 2.8 2.8h10c1.5 0 2.8-1.2 2.8-2.8v-3.6h-1.5V17zM14.9 3v1.5h3.7l-6.4 6.4 1.1 1.1 6.4-6.4v3.7h1.5V3h-6.3z"/>
						</svg>
					</a>
				)}
			</div>
			<div className="wc-block-components-radio-control" style={{ border: '1px solid #ddd', borderRadius: '4px', marginTop: '1em' }}>
				<label className={`wc-block-components-radio-control__option ${selectedInsurance === "" ? 'wc-block-components-radio-control__option-checked' : ''}`}
					style={{ borderBottom: '1px solid #ddd', margin: 0, padding: '.875em .875em .875em 3.5em', display: 'flex', alignItems: 'flex-start' }}>
					<input type="radio" name="shipping-insurance" className="wc-block-components-radio-control__input" value="" checked={selectedInsurance === ""} onChange={handleChange} style={{ marginTop: '3px' }} />
					<div className="wc-block-components-radio-control__option-layout">
						<div className="wc-block-components-radio-control__label-group">
							<span className="wc-block-components-radio-control__label">
								{__('No insurance', 'shipping-insurance-manager')}
							</span>
						</div>
						<span className="wc-block-components-radio-control__description">
							{__('Your shipment will not be insured.', 'shipping-insurance-manager')}
						</span>
					</div>
				</label>
				{filteredPackages.map((package_, index) => {
					return (
						<label key={package_.id}
							className={`wc-block-components-radio-control__option ${String(selectedInsurance) === String(package_.id) ? 'wc-block-components-radio-control__option-checked' : ''}`}
							style={{
								borderBottom: index === filteredPackages.length - 1 ? 'none' : '1px solid #ddd',
								margin: 0,
								paddingLeft: '3.5em',
								display: 'flex',
								alignItems: 'flex-start',
								...(index === filteredPackages.length - 1 ? {
									borderBottomLeftRadius: '4px',
									borderBottomRightRadius: '4px'
								} : {})
							}}
						>
							<input type="radio" name="shipping-insurance" className="wc-block-components-radio-control__input" value={String(package_.id)} checked={selectedInsurance === String(package_.id)} onChange={handleChange} style={{ marginTop: '3px' }} />
							<div className="wc-block-components-radio-control__option-layout">
								<div className="wc-block-components-radio-control__label-group">
									<span className="wc-block-components-radio-control__label">
										{package_.name}
									</span>
									<span className="wc-block-components-radio-control__secondary-label">
										<span className="wc-block-components-formatted-money-amount">
											{package_.type === 'percentage' ? (`${package_.amount}%`) : (<span dangerouslySetInnerHTML={{ __html: package_.amount_formatted }} />)}
										</span>
									</span>
								</div>
								{package_.description && (
									<span className="wc-block-components-radio-control__description">
										{package_.description}
									</span>
								)}
							</div>
						</label>
					);
				})}
			</div>
		</div>
	);
};

registerCheckoutBlock({ metadata, component: Block });
